import { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Platform,
  FlatList,
} from 'react-native';
import { useRouter } from 'expo-router';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { MapPin, Navigation, Map as MapIcon, Home } from 'lucide-react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { FLAGS } from '@/lib/featureFlags';

interface Gym {
  id: string;
  google_place_id: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  is_claimed: boolean;
}

export default function MapTab() {
  const router = useRouter();
  const { profile } = useAuth();
  const { theme } = useTheme();
  const [gyms, setGyms] = useState<Gym[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedGym, setSelectedGym] = useState<Gym | null>(null);

  // V1: Map disabled — redirect to home
  useEffect(() => {
    if (!FLAGS.V1_MAP_ENABLED) {
      router.replace('/(tabs)/home');
    }
  }, [router]);

  useEffect(() => {
    loadGyms();
  }, []);

  const loadGyms = async () => {
    const { data } = await supabase
      .from('gyms')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50);

    if (data) {
      setGyms(data);
    }
    setLoading(false);
  };

  const handleCheckIn = async (gymId: string) => {
    const { error } = await supabase.from('gym_check_ins').insert({
      user_id: profile!.id,
      gym_id: gymId,
    });

    if (error) {
      Alert.alert('Oops', "Couldn't check in. Please try again.");
    } else {
      Alert.alert('Recorded', 'Check-in Recorded');
      setSelectedGym(null);
    }
  };

  const setHomeGym = async (gym: Gym) => {
    const { error } = await supabase
      .from('profiles')
      .update({ home_gym_id: gym.id })
      .eq('id', profile!.id);

    if (error) {
      Alert.alert('Oops', "Couldn't set your home gym. Please try again.");
    } else {
      await supabase.from('user_gym_history').upsert({
        user_id: profile!.id,
        gym_id: gym.id,
        type: 'home',
        last_used_at: new Date().toISOString(),
      });

      Alert.alert('Set', `Home Gym Set`);
      setSelectedGym(null);
    }
  };

  const isHomeGym = (gymId: string) => {
    return profile?.home_gym_id === gymId;
  };

  if (loading) {
    return (
      <View style={[styles.centerContainer, { backgroundColor: theme.colors.background }]}>
        <ActivityIndicator size="large" color={theme.colors.accent} />
      </View>
    );
  }

  const renderGym = ({ item }: { item: Gym }) => (
    <TouchableOpacity
      style={[
        styles.gymCard,
        { backgroundColor: theme.colors.surface, borderColor: theme.colors.border },
        selectedGym?.id === item.id && { borderColor: theme.colors.accent, backgroundColor: theme.colors.surfaceVariant },
      ]}
      onPress={() => setSelectedGym(item)}>
      <View style={styles.gymCardHeader}>
        <View style={[styles.iconContainer, { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.accent }]}>
          <MapPin size={28} color={theme.colors.accent} />
        </View>
        <View style={styles.gymCardInfo}>
          <Text style={[styles.gymName, { color: theme.colors.text }]}>{item.name}</Text>
          <Text style={[styles.gymAddress, { color: theme.colors.textSecondary }]}>{item.address || 'Address not available'}</Text>
          <View style={styles.badgesRow}>
            {item.is_claimed && (
              <View style={[styles.claimedBadge, { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.warning }]}>
                <Text style={[styles.claimedText, { color: theme.colors.warning }]}>✓ Verified</Text>
              </View>
            )}
            {isHomeGym(item.id) && (
              <View style={[styles.homeBadge, { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.success }]}>
                <Home size={12} color={theme.colors.success} />
                <Text style={[styles.homeText, { color: theme.colors.success }]}>Home Gym</Text>
              </View>
            )}
          </View>
        </View>
      </View>
      {selectedGym?.id === item.id && (
        <View style={styles.actionsContainer}>
          <TouchableOpacity
            style={[styles.checkInButton, { backgroundColor: theme.colors.accent }]}
            onPress={() => handleCheckIn(item.id)}>
            <Navigation size={18} color={theme.colors.text} />
            <Text style={[styles.checkInButtonText, { color: theme.colors.accentForeground }]}>Check In</Text>
          </TouchableOpacity>
          {!isHomeGym(item.id) && (
            <TouchableOpacity
              style={[styles.homeGymButton, { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.success }]}
              onPress={() => setHomeGym(item)}>
              <Home size={18} color={theme.colors.success} />
              <Text style={[styles.homeGymButtonText, { color: theme.colors.success }]}>Set as Home</Text>
            </TouchableOpacity>
          )}
        </View>
      )}
    </TouchableOpacity>
  );

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <LinearGradient colors={[theme.colors.background, theme.colors.surfaceVariant]} style={[styles.header, { borderBottomColor: theme.colors.border }]}>
        <Text style={[styles.headerTitle, { color: theme.colors.accent }]}>Gyms Near You</Text>
        <Text style={[styles.headerSubtitle, { color: theme.colors.textSecondary }]}>{gyms.length} gyms available</Text>
      </LinearGradient>

      {Platform.OS === 'web' && (
        <View style={[styles.webNotice, { backgroundColor: theme.colors.surfaceVariant, borderColor: theme.colors.accent }]}>
          <MapIcon size={20} color={theme.colors.accent} />
          <Text style={[styles.webNoticeText, { color: theme.colors.accent }]}>
            Interactive map view available on iOS/Android app
          </Text>
        </View>
      )}

      <FlatList
        data={gyms}
        renderItem={renderGym}
        keyExtractor={(item) => item.id}
        contentContainerStyle={styles.listContent}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <MapPin size={48} color={theme.colors.textTertiary} />
            <Text style={[styles.emptyText, { color: theme.colors.textSecondary }]}>No gyms found</Text>
            <Text style={[styles.emptySubtext, { color: theme.colors.textTertiary }]}>
              Be the first to add a gym in your area
            </Text>
          </View>
        }
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  header: {
    paddingTop: 60,
    paddingBottom: 20,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: '900',
    letterSpacing: 2,
  },
  headerSubtitle: {
    fontSize: 14,
    marginTop: 4,
  },
  webNotice: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderWidth: 1,
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginHorizontal: 16,
    marginTop: 16,
    borderRadius: 2,
  },
  webNoticeText: {
    fontSize: 13,
    fontWeight: '600',
  },
  listContent: {
    padding: 16,
  },
  gymCard: {
    borderRadius: 2,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
  },
  gymCardHeader: {
    flexDirection: 'row',
    gap: 12,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: 1,
  },
  gymCardInfo: {
    flex: 1,
  },
  gymName: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 4,
  },
  gymAddress: {
    fontSize: 13,
    marginBottom: 6,
  },
  badgesRow: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  },
  claimedBadge: {
    borderWidth: 1,
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 4,
    alignSelf: 'flex-start',
  },
  claimedText: {
    fontSize: 11,
    fontWeight: '700',
  },
  homeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    borderWidth: 1,
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  homeText: {
    fontSize: 11,
    fontWeight: '700',
  },
  actionsContainer: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 12,
  },
  checkInButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    paddingVertical: 12,
    borderRadius: 0,
  },
  checkInButtonText: {
    fontSize: 14,
    fontWeight: '700',
  },
  homeGymButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    borderWidth: 2,
    paddingVertical: 12,
    borderRadius: 0,
  },
  homeGymButtonText: {
    fontSize: 14,
    fontWeight: '700',
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '700',
    marginTop: 16,
  },
  emptySubtext: {
    fontSize: 14,
    marginTop: 8,
    textAlign: 'center',
  },
});
